'use client';

import React, { useState, useEffect, useRef } from 'react';
import Link from 'next/link';
import { useRouter, usePathname } from 'next/navigation';
import AppImage from '@/components/ui/AppImage';
import LanguageSelector from '@/components/LanguageSelector';

const services = [
{
  label: 'Container Transport',
  href: '/container-transport',
  image: "/assets/images/remote/photo-1698862070748-30ec9de62d01.jpg",
  alt: 'Container ship at port',
  icon: '🚢',
  desc: 'FCL door-to-door worldwide'
},
{
  label: 'Less Container Load',
  href: '/lcl-transport',
  image: "/assets/images/remote/photo-1676848844775-a71f71f53428.jpg",
  alt: 'Warehouse with cargo boxes',
  icon: '📦',
  desc: 'Cost-efficient shared containers'
},
{
  label: 'Airfreight Transport',
  href: '/airfreight-transport',
  image: "/assets/images/remote/photo-1663517334988-f5c1c10ebccc.jpg",
  alt: 'Cargo aircraft on tarmac',
  icon: '✈️',
  desc: 'Express & standard air cargo'
},
{
  label: 'Inland Transport',
  href: '/inland-transport',
  image: "/assets/images/remote/photo-1728503151088-c520cba8e7c8.jpg",
  alt: 'Freight truck on highway',
  icon: '🚛',
  desc: 'Road & rail across Europe'
},
{
  label: 'Customs Representation',
  href: '/customs-representation',
  image: "/assets/images/remote/rocket-gen-img-19126c266-1764670092970.png",
  alt: 'Customs officer reviewing documents',
  icon: '📋',
  desc: 'Import/export declarations'
},
{
  label: 'Port Operations',
  href: '/port-operations',
  image: "/assets/images/remote/photo-1624020818821-80382753c325.jpg",
  alt: 'Port crane at night',
  icon: '⚓',
  desc: 'Stevedoring & cargo handling'
},
{
  label: 'Cargo Insurance',
  href: '/cargo-insurance',
  image: "/assets/images/remote/rocket-gen-img-1cee7b5b0-1772514470002.png",
  alt: 'Secured cargo in warehouse',
  icon: '🛡️',
  desc: 'Comprehensive transit cover'
},
{
  label: 'Bank Guarantee',
  href: '/bank-guarantee',
  image: "/assets/images/remote/rocket-gen-img-1976f44a5-1775648525249.png",
  alt: 'Signing financial documents',
  icon: '🏦',
  desc: 'Financial security instruments'
}];


const Header: React.FC = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [usefulInfoOpen, setUsefulInfoOpen] = useState(false);
  const [aboutOpen, setAboutOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileUsefulInfoOpen, setMobileUsefulInfoOpen] = useState(false);
  const [mobileAboutOpen, setMobileAboutOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const usefulInfoRef = useRef<HTMLDivElement>(null);
  const aboutRef = useRef<HTMLDivElement>(null);
  const router = useRouter();
  const pathname = usePathname();

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    if (mobileOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {document.body.style.overflow = '';};
  }, [mobileOpen]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setServicesOpen(false);
      }
      if (usefulInfoRef.current && !usefulInfoRef.current.contains(e.target as Node)) {
        setUsefulInfoOpen(false);
      }
      if (aboutRef.current && !aboutRef.current.contains(e.target as Node)) {
        setAboutOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleServicesClick = () => {
    setServicesOpen((v) => !v);
  };

  const handleServicesScrollOnly = (e: React.MouseEvent) => {
    e.preventDefault();
    if (pathname === '/home-page' || pathname === '/') {
      const el = document.getElementById('services');
      if (el) {
        el.scrollIntoView({ behavior: 'smooth' });
        setServicesOpen(false);
        setMobileOpen(false);
      }
    } else {
      router.push('/home-page#services');
      setServicesOpen(false);
      setMobileOpen(false);
    }
  };

  const topLinks = [
  { label: 'News & Promotions', href: '/news-promotions' },
  { label: 'Media Releases', href: '/media-releases' },
  { label: 'Contacts', href: '/contacts' }];

  const aboutLinks = [
  { label: 'About Us', href: '/about', desc: 'Company overview & key facts' },
  { label: 'History', href: '/about/history', desc: 'Our journey since 2001' },
  { label: 'Mission', href: '/about/mission', desc: 'What drives us forward' },
  { label: 'Vision', href: '/about/vision', desc: 'Vision 2030 strategy' },
  { label: 'Membership', href: '/about/membership', desc: 'IATA, FIATA, UN Global Compact & more' },
  { label: 'Quality Management', href: '/about/quality-management', desc: 'ISO 9001:2015 certified' },
  { label: 'Business Ethics', href: '/about/business-ethics', desc: 'UN Global Compact principles' }];


  const certificationLinks = [
  { label: 'FIATA', desc: 'International Federation of Freight Forwarders', href: '/home-page#useful-info' },
  { label: 'SGS', desc: 'Société Générale de Surveillance', href: '/home-page#useful-info' },
  { label: 'IATA', desc: 'International Air Transport Association', href: '/home-page#useful-info' },
  { label: 'NSBS', desc: 'National Standardization Body of Bulgaria', href: '/home-page#useful-info' }];


  const containerLinks = [
  { label: 'Standard Container', desc: "20\' & 40\' Dry Container", id: 'standard-container', color: '#60A5FA' },
  { label: 'Open Top Container', desc: 'Removable tarpaulin roof', id: 'open-top-container', color: '#34D399' },
  { label: 'Refrigerated Container', desc: 'Reefer — temp controlled', id: 'refrigerated-container', color: '#818CF8' },
  { label: 'Flat Rack Container', desc: 'No side walls or roof', id: 'flat-rack-container', color: '#F59E0B' },
  { label: 'Platform Container', desc: 'Floor only — max flexibility', id: 'platform-container', color: '#F87171' }];


  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled ?
      'backdrop-blur-xl border-b shadow-[0_4px_24px_rgba(15,35,64,0.25)]' :
      'backdrop-blur-md border-b'}`
      }
      style={{
        backgroundColor: scrolled ? 'rgba(10,21,53,0.98)' : 'rgba(10,21,53,0.96)',
        borderBottomColor: 'rgba(255,255,255,0.08)'
      }}>
      
      {/* Top bar */}
      <div className="hidden lg:block" style={{ backgroundColor: '#0F2340', borderBottom: '1px solid rgba(255,255,255,0.08)' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between py-3">
          <div className="flex items-center gap-8">
            {topLinks.map((link) =>
            <a
              key={link.label}
              href={link.href}
              className="flex items-center gap-1 text-sm font-medium transition-colors duration-200 font-body tracking-wide uppercase"
              style={{ color: '#FFFFFF' }}
              onMouseEnter={(e) => e.currentTarget.style.color = '#FFFFFF'}
              onMouseLeave={(e) => e.currentTarget.style.color = '#FFFFFF'}>
              
                {link.label}
              </a>
            )}
          </div>
          <div className="flex items-center gap-6">
            {/* THE COMPANY dropdown in top bar */}
            <div className="relative" ref={aboutRef}>
              <button
                onClick={() => setAboutOpen((v) => !v)}
                className="flex items-center gap-1 text-sm font-medium transition-colors duration-200 font-body tracking-wide"
                style={{ color: '#FFFFFF', background: 'none', border: 'none', cursor: 'pointer' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#FFFFFF'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#FFFFFF'}>
                THE COMPANY
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                className={`w-3 h-3 transition-transform duration-200 ${aboutOpen ? 'rotate-180' : ''}`}>
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>

              {aboutOpen &&
              <div
                className="absolute top-full right-0 mt-3 rounded-2xl shadow-2xl z-50"
                style={{
                  background: '#FFFFFF',
                  border: '1px solid rgba(15,35,64,0.1)',
                  backdropFilter: 'blur(24px)',
                  width: '320px',
                  boxShadow: '0 8px 40px rgba(15,35,64,0.15)'
                }}>
                  <div className="px-5 py-4" style={{ borderBottom: '1px solid rgba(15,35,64,0.08)' }}>
                    <p className="text-xs uppercase tracking-widest font-body mb-0.5" style={{ color: '#1B6B8A' }}>The Company</p>
                    <p className="text-sm font-body" style={{ color: '#6A85A0' }}>Learn more about World Transport Overseas</p>
                  </div>
                  <div className="p-2">
                    {aboutLinks.map((item) =>
                  <Link
                    key={item.href}
                    href={item.href}
                    onClick={() => setAboutOpen(false)}
                    className="flex items-start gap-3 px-3 py-2.5 rounded-xl transition-all duration-200"
                    style={{ color: '#0F2340' }}
                    onMouseEnter={(e) => {e.currentTarget.style.background = 'rgba(27,107,138,0.08)';}}
                    onMouseLeave={(e) => {e.currentTarget.style.background = 'transparent';}}>
                        <div>
                          <p className="text-sm font-semibold font-body leading-tight" style={{ color: '#0F2340' }}>{item.label}</p>
                          <p className="text-xs font-body mt-0.5" style={{ color: '#6A85A0' }}>{item.desc}</p>
                        </div>
                      </Link>
                  )}
                  </div>
                </div>
              }
            </div>

            {/* SERVICES dropdown in top bar */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={handleServicesClick}
                className="flex items-center gap-1 text-sm font-medium transition-colors duration-200 font-body tracking-wide"
                style={{ color: '#FFFFFF', background: 'none', border: 'none', cursor: 'pointer' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#FFFFFF'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#FFFFFF'}>
                SERVICES
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  className={`w-3 h-3 transition-transform duration-200 ${servicesOpen ? 'rotate-180' : ''}`}>
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>

              {servicesOpen &&
              <div
                className="absolute top-full right-0 mt-3 rounded-2xl shadow-2xl overflow-hidden z-50"
                style={{
                  background: '#FFFFFF',
                  border: '1px solid rgba(15,35,64,0.1)',
                  backdropFilter: 'blur(24px)',
                  width: '680px',
                  boxShadow: '0 8px 40px rgba(15,35,64,0.15)'
                }}>
                
                  {/* Mega-menu header */}
                  <div className="px-6 py-4 flex items-center justify-between" style={{ borderBottom: '1px solid rgba(15,35,64,0.08)' }}>
                    <div>
                      <p className="text-xs uppercase tracking-widest font-body mb-0.5" style={{ color: '#1B6B8A' }}>Our Services</p>
                      <p className="text-sm font-body" style={{ color: '#6A85A0' }}>End-to-end freight solutions across all transport modes</p>
                    </div>
                    <button
                    onClick={handleServicesScrollOnly}
                    className="text-xs font-body px-3 py-1.5 rounded-full transition-all duration-200 flex items-center gap-1.5"
                    style={{ background: 'rgba(27,107,138,0.1)', color: '#1B6B8A', border: '1px solid rgba(27,107,138,0.2)' }}
                    onMouseEnter={(e) => {e.currentTarget.style.background = 'rgba(27,107,138,0.18)';}}
                    onMouseLeave={(e) => {e.currentTarget.style.background = 'rgba(27,107,138,0.1)';}}>
                      View all
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14M13 5l7 7-7 7" />
                      </svg>
                    </button>
                  </div>

                  {/* Service cards grid */}
                  <div className="p-4 grid grid-cols-4 gap-2">
                    {services.map((s) =>
                  <Link
                    key={s.href}
                    href={s.href}
                    onClick={() => setServicesOpen(false)}
                    className="group relative rounded-xl overflow-hidden flex flex-col"
                    style={{ height: '130px' }}>
                    
                        {/* Background image */}
                        <div className="absolute inset-0">
                          <AppImage
                        src={s.image}
                        alt={s.alt}
                        fill
                        className="object-cover transition-transform duration-500 group-hover:scale-110"
                        sizes="170px"
                        unoptimized />
                      
                          <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/10" />
                          <div
                        className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                        style={{ background: 'rgba(27,107,138,0.25)' }} />
                      
                        </div>

                        {/* Icon badge */}
                        <div className="absolute top-2 right-2 w-7 h-7 rounded-full flex items-center justify-center text-sm"
                    style={{ background: 'rgba(255,255,255,0.85)', backdropFilter: 'blur(4px)', border: '1px solid rgba(255,255,255,0.5)' }}>
                          {s.icon}
                        </div>

                        {/* Text */}
                        <div className="absolute bottom-0 left-0 right-0 p-2.5">
                          <p className="text-white text-xs font-bold font-body leading-tight" style={{ textShadow: '0 1px 6px rgba(0,0,0,0.9)' }}>
                            {s.label}
                          </p>
                          <p className="text-xs font-body mt-0.5 leading-tight opacity-0 group-hover:opacity-100 transition-opacity duration-300" style={{ color: '#C8D8F0', textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                            {s.desc}
                          </p>
                        </div>

                        {/* Bottom accent line */}
                        <div
                      className="absolute bottom-0 left-0 right-0 h-0.5 scale-x-0 origin-left group-hover:scale-x-100 transition-transform duration-300"
                      style={{ background: '#1B6B8A' }} />
                    
                      </Link>
                  )}
                  </div>

                  {/* Footer CTA */}
                  <div className="px-4 pb-4">
                    <Link
                    href="/transport-quote"
                    onClick={() => setServicesOpen(false)}
                    className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-semibold font-body transition-all duration-200"
                    style={{ background: '#1A5FAB', color: '#FFFFFF', border: '1px solid rgba(26,95,171,0.3)' }}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M9 5H7a2 2 0 0 0-2 2v12a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2" />
                        <rect x="9" y="3" width="6" height="4" rx="1" />
                        <path d="M9 12h6M9 16h4" />
                      </svg>
                      Get a Free Quote
                    </Link>
                  </div>
                </div>
              }
            </div>

            {/* USEFUL INFORMATION dropdown in top bar */}
            <div className="relative" ref={usefulInfoRef}>
              <button
                onClick={() => setUsefulInfoOpen((v) => !v)}
                className="flex items-center gap-1 text-sm font-medium transition-colors duration-200 font-body tracking-wide"
                style={{ color: '#FFFFFF', background: 'none', border: 'none', cursor: 'pointer' }}
                onMouseEnter={(e) => e.currentTarget.style.color = '#FFFFFF'}
                onMouseLeave={(e) => e.currentTarget.style.color = '#FFFFFF'}>
                USEFUL INFORMATION
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                  className={`w-3 h-3 transition-transform duration-200 ${usefulInfoOpen ? 'rotate-180' : ''}`}>
                  <path d="M6 9l6 6 6-6" />
                </svg>
              </button>

              {usefulInfoOpen &&
              <div
                className="absolute top-full right-0 mt-3 rounded-2xl shadow-2xl z-50"
                style={{
                  background: '#FFFFFF',
                  border: '1px solid rgba(15,35,64,0.1)',
                  backdropFilter: 'blur(24px)',
                  width: '620px',
                  boxShadow: '0 8px 40px rgba(15,35,64,0.15)'
                }}>
                  {/* Header */}
                  <div className="px-6 py-4" style={{ borderBottom: '1px solid rgba(15,35,64,0.08)' }}>
                    <p className="text-xs uppercase tracking-widest font-body mb-0.5" style={{ color: '#1B6B8A' }}>Useful Information</p>
                    <p className="text-sm font-body" style={{ color: '#6A85A0' }}>Certifications & container type guides</p>
                  </div>

                  <div className="p-5 grid grid-cols-2 gap-5">
                    {/* Certifications column */}
                    <div>
                      <p className="text-xs uppercase tracking-widest font-body mb-3 font-semibold" style={{ color: '#6A85A0' }}>Certifications</p>
                      <div className="space-y-1">
                        {certificationLinks.map((item) =>
                      <a
                        key={item.label}
                        href={item.href}
                        onClick={() => setUsefulInfoOpen(false)}
                        className="flex items-start gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group"
                        style={{ color: '#0F2340' }}
                        onMouseEnter={(e) => {e.currentTarget.style.background = 'rgba(27,107,138,0.08)';}}
                        onMouseLeave={(e) => {e.currentTarget.style.background = 'transparent';}}>
                            <span className="w-10 h-7 rounded-lg flex items-center justify-center text-xs font-bold flex-shrink-0 mt-0.5" style={{ background: 'rgba(27,107,138,0.1)', color: '#1B6B8A', border: '1px solid rgba(27,107,138,0.2)' }}>
                              {item.label}
                            </span>
                            <div>
                              <p className="text-sm font-semibold font-body leading-tight" style={{ color: '#0F2340' }}>{item.label}</p>
                              <p className="text-xs font-body mt-0.5 leading-tight" style={{ color: '#6A85A0' }}>{item.desc}</p>
                            </div>
                          </a>
                      )}
                      </div>
                    </div>

                    {/* Container Types column */}
                    <div>
                      <p className="text-xs uppercase tracking-widest font-body mb-3 font-semibold" style={{ color: '#6A85A0' }}>Container Types</p>
                      <div className="space-y-1">
                        {containerLinks.map((item) =>
                      <a
                        key={item.id}
                        href={`/useful-info?type=${item.id}`}
                        onClick={() => setUsefulInfoOpen(false)}
                        className="flex items-start gap-3 px-3 py-2.5 rounded-xl transition-all duration-200"
                        style={{ color: '#0F2340' }}
                        onMouseEnter={(e) => {e.currentTarget.style.background = `${item.color}15`;}}
                        onMouseLeave={(e) => {e.currentTarget.style.background = 'transparent';}}>
                            <span className="w-2.5 h-2.5 rounded-full flex-shrink-0 mt-1.5" style={{ background: item.color }} />
                            <div>
                              <p className="text-sm font-semibold font-body leading-tight" style={{ color: '#0F2340' }}>{item.label}</p>
                              <p className="text-xs font-body mt-0.5 leading-tight" style={{ color: '#6A85A0' }}>{item.desc}</p>
                            </div>
                          </a>
                      )}
                      </div>
                    </div>
                  </div>

                  {/* Footer */}
                  <div className="px-5 pb-4">
                    <a
                    href="/useful-info"
                    onClick={() => setUsefulInfoOpen(false)}
                    className="flex items-center justify-center gap-2 w-full py-2.5 rounded-xl text-sm font-semibold font-body transition-all duration-200"
                    style={{ background: 'rgba(27,107,138,0.1)', color: '#1B6B8A', border: '1px solid rgba(27,107,138,0.2)' }}
                    onMouseEnter={(e) => {e.currentTarget.style.background = 'rgba(27,107,138,0.18)';}}
                    onMouseLeave={(e) => {e.currentTarget.style.background = 'rgba(27,107,138,0.1)';}}>
                      View all container guides
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M5 12h14M13 5l7 7-7 7" />
                      </svg>
                    </a>
                  </div>
                </div>
              }
            </div>

            <LanguageSelector />
          </div>
        </div>
      </div>

      {/* Main nav */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 lg:h-20">
          {/* Logo */}
          <Link href="/home-page" className="flex items-center gap-2 lg:gap-3 shrink min-w-0">
            <AppImage
              src="/assets/images/logo_letters_3_-1775650123755.png"
              alt="World Transport Overseas logo"
              width={260}
              height={80}
              priority
              className="h-12 sm:h-14 lg:h-24 w-auto max-w-[148px] sm:max-w-[180px] lg:max-w-none"
              unoptimized />
            
            <span className="hidden lg:block font-bold font-body tracking-widest uppercase" style={{ color: '#FFFFFF', letterSpacing: '0.08em', fontSize: '1.6rem', whiteSpace: 'nowrap', marginBottom: '6px' }}>
              WORLD TRANSPORT OVERSEAS
            </span>
          </Link>

          {/* Desktop nav — only Portal Registration and MY WTO Login remain */}
          <nav className="hidden lg:flex items-center gap-8">
            <a
              href="/portal-registration"
              className="flex items-center gap-2 px-6 py-2.5 rounded-full text-sm font-semibold hover:opacity-90 transition-all duration-200 font-body shadow-lg"
              style={{ backgroundColor: '#DC2626', color: '#FFFFFF', border: '1px solid rgba(220,38,38,0.6)' }}>
              
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <line x1="19" y1="8" x2="19" y2="14" />
                <line x1="22" y1="11" x2="16" y2="11" />
              </svg>
              Portal Registration
            </a>
            <a
              href="https://www-w49prd.wisegrid.net/Portals/NEO/Mobile#/login"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-6 py-2.5 rounded-full text-white text-sm font-semibold hover:opacity-90 transition-all duration-200 font-body shadow-lg"
              style={{ backgroundColor: '#1A5FAB', border: '1px solid rgba(36,112,200,0.4)' }}>
              
              <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
              MY WTO Login
            </a>
          </nav>

          {/* Mobile hamburger */}
          <button
            onClick={() => setMobileOpen((v) => !v)}
            className="lg:hidden flex items-center gap-2 px-3 py-2 rounded-full border font-body shrink-0"
            style={{ borderColor: 'rgba(255,255,255,0.25)', background: 'rgba(255,255,255,0.1)', color: '#FFFFFF' }}
            aria-label={mobileOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={mobileOpen}
            aria-controls="mobile-menu">
            
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M4 6h16M4 12h16M4 18h16" />
            </svg>
            <span className="text-sm">Menu</span>
          </button>
        </div>
      </div>

      {/* Mobile menu overlay */}
      <div
        id="mobile-menu"
        className={`lg:hidden absolute left-0 right-0 top-full h-[calc(100vh-4rem)] backdrop-blur-xl z-[60] flex flex-col overflow-y-auto ${
        mobileOpen ? 'opacity-100 visible' : 'opacity-0 invisible'}`
        }
        style={{ background: 'rgba(255,255,255,0.98)', width: '100vw', maxWidth: '100vw' }}>
        
        <div className="flex items-center justify-between px-6 py-5 border-b" style={{ borderColor: 'rgba(15,35,64,0.1)' }}>
          <AppImage
            src="/assets/images/logo-dark.png"
            alt="World Transport Overseas logo"
            width={160}
            height={160}
            className="h-12 w-auto max-w-[150px] object-contain"
            unoptimized />
          
          <button
            onClick={() => setMobileOpen(false)}
            className="p-2 rounded-full border"
            style={{ borderColor: 'rgba(15,35,64,0.15)', background: 'rgba(15,35,64,0.06)', color: '#0F2340' }}
            aria-label="Close menu">
            
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M18 6 6 18M6 6l12 12" />
            </svg>
          </button>
        </div>
        <nav className="flex-1 flex flex-col px-6 py-8 gap-2">
          {/* Mobile About accordion */}
          <div>
            <button
              onClick={() => setMobileAboutOpen((v) => !v)}
              className="flex items-center justify-between w-full text-2xl font-medium font-body py-2"
              style={{ color: '#0F2340', background: 'none', border: 'none', cursor: 'pointer' }}>
              THE COMPANY
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
              className={`w-5 h-5 transition-transform duration-200 ${mobileAboutOpen ? 'rotate-180' : ''}`}>
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            {mobileAboutOpen &&
            <div className="mt-1 mb-3 space-y-1 pl-2">
                {aboutLinks.map((item) =>
              <Link
                key={item.href}
                href={item.href}
                onClick={() => {setMobileOpen(false);setMobileAboutOpen(false);}}
                className="flex flex-col px-3 py-2.5 rounded-xl transition-all duration-200"
                style={{ color: '#0F2340' }}
                onMouseEnter={(e) => {e.currentTarget.style.background = 'rgba(27,107,138,0.08)';}}
                onMouseLeave={(e) => {e.currentTarget.style.background = 'transparent';}}>
                    <span className="text-base font-semibold font-body" style={{ color: '#0F2340' }}>{item.label}</span>
                    <span className="text-xs font-body" style={{ color: '#6A85A0' }}>{item.desc}</span>
                  </Link>
              )}
              </div>
            }
          </div>

          {/* Mobile Services accordion */}
          <div>
            <button
              onClick={() => setMobileServicesOpen((v) => !v)}
              className="flex items-center justify-between w-full text-2xl font-medium font-body py-2"
              style={{ color: '#0F2340', background: 'none', border: 'none', cursor: 'pointer' }}>
              
              SERVICES
              <svg
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
                className={`w-5 h-5 transition-transform duration-200 ${mobileServicesOpen ? 'rotate-180' : ''}`}>
                
                <path d="M6 9l6 6 6-6" />
              </svg>
            </button>
            {mobileServicesOpen &&
            <div className="mt-2 mb-3">
                {/* Scroll to services section button */}
                <button
                onClick={handleServicesScrollOnly}
                className="flex items-center gap-2 w-full py-2.5 px-3 rounded-xl mb-2 text-sm font-body font-medium"
                style={{ background: 'rgba(27,107,138,0.1)', color: '#1B6B8A', border: '1px solid rgba(27,107,138,0.2)' }}>
                
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M12 5v14M5 12l7 7 7-7" />
                  </svg>
                  View all services on page
                </button>
                {/* Service cards in 2-col grid */}
                <div className="grid grid-cols-2 gap-2">
                  {services.map((s) =>
                <Link
                  key={s.href}
                  href={s.href}
                  onClick={() => {setMobileOpen(false);setMobileServicesOpen(false);}}
                  className="group relative rounded-xl overflow-hidden flex flex-col"
                  style={{ height: '90px' }}>
                  
                      <div className="absolute inset-0">
                        <AppImage
                      src={s.image}
                      alt={s.alt}
                      fill
                      className="object-cover"
                      sizes="160px"
                      unoptimized />
                    
                        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-black/10" />
                      </div>
                      <div className="absolute top-1.5 right-1.5 text-base">{s.icon}</div>
                      <div className="absolute bottom-0 left-0 right-0 p-2">
                        <p className="text-white text-xs font-bold font-body leading-tight" style={{ textShadow: '0 1px 4px rgba(0,0,0,0.9)' }}>
                          {s.label}
                        </p>
                      </div>
                    </Link>
                )}
                </div>
              </div>
            }
          </div>

          <a
            href="#useful-info"
            onClick={() => setMobileOpen(false)}
            className="text-2xl font-medium transition-colors font-body py-2"
            style={{ color: '#0F2340' }}>
            
            USEFUL INFORMATION
          </a>

          <div className="pt-6 flex flex-col gap-4" style={{ borderTop: '1px solid rgba(15,35,64,0.1)' }}>
            {topLinks.map((link) =>
            <a
              key={link.label}
              href={link.href}
              onClick={() => setMobileOpen(false)}
              className="text-base transition-colors font-body"
              style={{ color: '#3A5070' }}>
              
                {link.label}
              </a>
            )}
          </div>
          <a
            href="/portal-registration"
            className="mt-4 flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-base font-semibold font-body shadow-lg"
            style={{ backgroundColor: '#DC2626', color: '#FFFFFF' }}>
            
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" />
              <circle cx="9" cy="7" r="4" />
              <line x1="19" y1="8" x2="19" y2="14" />
              <line x1="22" y1="11" x2="16" y2="11" />
            </svg>
            Portal Registration
          </a>
          <a
            href="https://www-w49prd.wisegrid.net/Portals/NEO/Mobile#/login"
            target="_blank"
            rel="noopener noreferrer"
            className="mt-2 flex items-center justify-center gap-2 px-6 py-3.5 rounded-full text-white text-base font-semibold font-body shadow-lg"
            style={{ backgroundColor: '#1A5FAB' }}>
            
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            MY WTO Login
          </a>
          <div className="mt-2 flex justify-center">
            <LanguageSelector compact theme="light" />
          </div>
        </nav>
      </div>
    </header>);

};

export default Header;
